import { useCallback, useEffect, useMemo, useState } from "react";

const EMPTY = Object.freeze({ tasks: {}, discussions: {} });
const storageKey = (meId) => (Number.isInteger(meId) ? `investing.unlinked.v1.${meId}` : null);

/** Only `{SYMBOL: {id, …}}` entries survive a read: storage is anyone's to edit. */
function entries(map, keep) {
  if (!map || typeof map !== "object" || Array.isArray(map)) return {};
  return Object.fromEntries(
    Object.entries(map)
      .filter(([symbol, value]) => typeof symbol === "string" && Number.isInteger(value?.id))
      .map(([symbol, value]) => [symbol, keep(value)]),
  );
}

// What a page needs to offer "Link task #N" (and who has it) or "Link
// discussion #N" — not the whole task.
const keepTask = (task) => ({
  id: task.id,
  assignee: typeof task.assignee?.username === "string" ? { username: task.assignee.username } : null,
});
const keepDiscussion = (discussion) => ({ id: discussion.id });

function load(key) {
  if (!key) return EMPTY;
  try {
    const raw = window.sessionStorage.getItem(key);
    const data = raw ? JSON.parse(raw) : null;
    return { tasks: entries(data?.tasks, keepTask), discussions: entries(data?.discussions, keepDiscussion) };
  } catch {
    return EMPTY;
  }
}

function save(key, state) {
  if (!key) return;
  try {
    if (Object.keys(state.tasks).length === 0 && Object.keys(state.discussions).length === 0) {
      window.sessionStorage.removeItem(key);
    } else {
      window.sessionStorage.setItem(key, JSON.stringify(state));
    }
  } catch {
    // Storage blocked (a private window, site data off): it lasts while
    // the app stays open.
  }
}

/**
 * The research tasks and discussions made for a company whose link to it
 * failed (spec walkthroughs 2 and 6): the task or discussion exists, the
 * company doesn't point at it yet. Offering "Link task #N" — never a fresh
 * Research this — is what keeps a second task (and a second agent working
 * the same company) from being made.
 *
 * One store for the whole Investing section (InvestingLayout puts it in
 * the outlet context), so the Research list and a company's page offer the
 * same "Link task #N"; keyed by symbol, so one company's never shows on
 * another's page; and kept in sessionStorage per person, so a reload or a
 * trip to the board keeps it for this tab. A new tab doesn't know it —
 * the dialogs say to check the board then.
 *
 * `{tasks, discussions, setTask(symbol, task|null), setDiscussion(symbol, discussion|null)}`.
 * Nothing is kept while `meId` is unknown.
 */
function useUnlinked(meId) {
  const key = storageKey(meId);
  const [state, setState] = useState(() => load(key));
  // Who's signed in changed (or became known): their own store.
  const [loadedFor, setLoadedFor] = useState(key);
  if (key !== loadedFor) {
    setLoadedFor(key);
    setState(load(key));
  }

  useEffect(() => {
    save(key, state);
  }, [key, state]);

  const put = useCallback((kind, keep, symbol, value) => {
    setState((prev) => {
      const next = { ...prev[kind] };
      if (value) next[symbol] = keep(value);
      else delete next[symbol];
      return { ...prev, [kind]: next };
    });
  }, []);
  const setTask = useCallback((symbol, task) => put("tasks", keepTask, symbol, task), [put]);
  const setDiscussion = useCallback(
    (symbol, discussion) => put("discussions", keepDiscussion, symbol, discussion),
    [put],
  );

  return useMemo(
    () => ({ tasks: state.tasks, discussions: state.discussions, setTask, setDiscussion }),
    [state, setTask, setDiscussion],
  );
}

export default useUnlinked;
